import { userErrorMessage } from '@wx-weiqi/contracts';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
  type FormEvent,
} from 'react';
import type { BoardPoint, FoxKifuRecordSummary, StoneColor } from '@wx-weiqi/contracts';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  CameraIcon,
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  ListBulletIcon,
  Pencil2Icon,
  ResetIcon,
  TargetIcon,
  LightningBoltIcon,
} from '@radix-ui/react-icons';

import { BoardSurface } from './BoardSurface';
import { BoardMoveProgress } from './BoardMoveProgress';
import { AiCandidatePanel } from './AiCandidatePanel';
import { AiAccountSelector } from './AiAccountSelector';
import { selectedAccount } from '../stores/ai-account-selection';
import {
  ReviewAnnotationLayer,
  type ReviewAnnotation,
  type ReviewAnnotationColor,
  type ReviewAnnotationTool,
  type ReviewBrushStroke,
  toggleReviewPointAnnotation,
} from './ReviewAnnotationLayer';
import { ReviewAnnotationPanel } from './ReviewAnnotationPanel';
import { useBoardPanelDrag } from './useBoardPanelDrag';
import { VerticalMoveSlider } from './VerticalMoveSlider';
import { aiVariationStoneLabels, presentAiCandidates } from '../domain/ai-candidate-presentation';
import {
  branchResearchVariation,
  isFlyingKnifeRecord,
  researchStoneLabels,
  researchVariationPrefix,
} from '../domain/fox-kifu-presentation';
import {
  ZHIZI_COMPUTE_MODE_LABELS,
  zhiziWeightLabel,
  ZHIZI_EXCLUSIVE_TIER_LABELS,
} from '../domain/zhizi-profile';
import { downloadBoardJpeg, foxBoardCaptureFilename } from '../services/board-capture';
import { FoxKifuReflectionCard } from './FoxKifuReflectionCard';
import {
  createFoxKifuReflectionState,
  foxKifuReflectionReducer,
} from '../domain/fox-kifu-reflection';
import type { FoxKifuSearchInput, FoxKifuStore } from '../stores/fox-kifu-store';
import './review-studio.css';
import './fox-kifu-studio.css';

interface FoxKifuPageProps {
  readonly store: FoxKifuStore;
  readonly onBack: () => void;
}

type MarkerMode = 'LAST_ONE' | 'LAST_FIVE' | 'ALL';

const EMPTY_BOARD = Array<null>(19 * 19).fill(null);
const MARKER_MODES: readonly MarkerMode[] = ['LAST_ONE', 'LAST_FIVE', 'ALL'];
const MARKER_LABELS: Readonly<Record<MarkerMode, string>> = {
  LAST_ONE: '最后1手',
  LAST_FIVE: '最后5手',
  ALL: '全部手数',
};

export const FOX_KIFU_STUDENT_TOOL_ORDER = Object.freeze([
  'MARKER',
  'RESEARCH',
  'ANNOTATION',
  'AI',
  'FOX_KIFU',
  'CAPTURE',
] as const);

export function foxKifuStatisticsPercent(value: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((Math.max(0, value) / total) * 1_000) / 10;
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function formatStartTime(value: string): string {
  return new Date(value).toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatGameDuration(value: number | null): string {
  if (value === null) return '未知';
  const hours = Math.floor(value / 3_600);
  const minutes = Math.floor((value % 3_600) / 60);
  const seconds = value % 60;
  return hours > 0
    ? `${hours}时${String(minutes).padStart(2, '0')}分`
    : `${minutes}分${String(seconds).padStart(2, '0')}秒`;
}

function playerForColor(record: FoxKifuRecordSummary | undefined, color: StoneColor) {
  if (record === undefined) return null;
  return color === 'BLACK' ? record.black : record.white;
}

function pointId(point: BoardPoint): string {
  return `${point.x}:${point.y}`;
}

export function FoxKifuPage({ store, onBack }: FoxKifuPageProps) {
  const state = useSyncExternalStore(store.subscribe, store.snapshot);
  const pageRef = useRef<HTMLElement>(null);
  const boardStageRef = useRef<HTMLDivElement>(null);
  const foxSearchPanel = useBoardPanelDrag();
  const [query, setQuery] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [activeSearch, setActiveSearch] = useState<FoxKifuSearchInput | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [moveNumber, setMoveNumber] = useState(0);
  const [markerMode, setMarkerMode] = useState<MarkerMode>('LAST_ONE');
  const [researchBase, setResearchBase] = useState<number | null>(null);
  const [variationMoves, setVariationMoves] = useState<readonly BoardPoint[]>([]);
  const [researchCursor, setResearchCursor] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [captureMessage, setCaptureMessage] = useState<string | null>(null);
  const [immersive, setImmersive] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiConnectionOpen, setAiConnectionOpen] = useState(false);
  const [aiChecking, setAiChecking] = useState(false);
  const [annotationOpen, setAnnotationOpen] = useState(false);
  const [annotationTool, setAnnotationTool] = useState<ReviewAnnotationTool>('BRUSH');
  const [annotationColor, setAnnotationColor] = useState<ReviewAnnotationColor>('RED');
  const [annotationWidth, setAnnotationWidth] = useState(7);
  const [annotations, setAnnotations] = useState<readonly ReviewAnnotation[]>([]);
  const [foxSearchOpen, setFoxSearchOpen] = useState(false);
  const [reflection, dispatchReflection] = useReducer(
    foxKifuReflectionReducer,
    undefined,
    createFoxKifuReflectionState,
  );
  const reflectionRecord =
    reflection.openRecordId === state.record?.record.recordId ? state.record.record : null;
  const selectedSummary = [
    ...(state.search?.records ?? []),
    ...(state.detailSearch?.records ?? []),
  ].find((record) => record.recordId === selectedId);
  const frames = state.record?.frames ?? [];
  const originalFrame = frames[Math.min(moveNumber, Math.max(0, frames.length - 1))];
  const boardSize = state.record?.record.boardSize ?? 19;
  const researching = researchBase !== null;
  const activeVariationMoves = useMemo(
    () => researchVariationPrefix(variationMoves, researchCursor),
    [researchCursor, variationMoves],
  );
  const stones = researching
    ? (state.research?.stones ?? originalFrame?.stones ?? EMPTY_BOARD)
    : (originalFrame?.stones ?? EMPTY_BOARD);
  const totalMoves = Math.max(0, frames.length - 1);
  const totalPages = Math.max(1, Math.ceil((state.search?.total ?? 0) / 2));
  const page = state.search?.page ?? 1;
  const detailPages = Math.max(1, Math.ceil((state.detailSearch?.total ?? 0) / 10));
  const detailPage = state.detailSearch?.page ?? 1;
  const searchInput = useMemo<FoxKifuSearchInput>(
    () => ({ query: query.trim(), from, to, pageSize: 2 }),
    [from, query, to],
  );
  const stoneLabels = useMemo(() => {
    if (researching && activeVariationMoves.length > 0) {
      return researchStoneLabels(activeVariationMoves, stones, boardSize);
    }
    const latestAtPoint = new Map<
      string,
      {
        readonly point: BoardPoint;
        readonly label: string;
        readonly color: StoneColor;
        order: number;
      }
    >();
    for (const frame of frames.slice(1, moveNumber + 1)) {
      if (frame.kind !== 'PLAY' || frame.point === null || frame.color === null) continue;
      if (stones[frame.point.y * boardSize + frame.point.x] !== frame.color) continue;
      latestAtPoint.set(pointId(frame.point), {
        point: frame.point,
        label: String(frame.moveNumber),
        color: frame.color,
        order: frame.moveNumber,
      });
    }
    const candidates = [...latestAtPoint.values()].sort((left, right) => left.order - right.order);
    const visible =
      markerMode === 'ALL' ? candidates : candidates.slice(markerMode === 'LAST_ONE' ? -1 : -5);
    return visible.map(({ point, label, color }) => ({ point, label, color }));
  }, [activeVariationMoves, boardSize, frames, markerMode, moveNumber, researching, stones]);
  const aiCandidates = useMemo(
    () => (aiEnabled ? presentAiCandidates(state.aiAnalysis) : []),
    [aiEnabled, state.aiAnalysis],
  );
  const aiVariationLabels = useMemo(
    () => aiVariationStoneLabels(state.aiVariation),
    [state.aiVariation],
  );
  const displayedStones = state.aiVariation?.stones ?? stones;
  const displayedLabels = state.aiVariation === null ? stoneLabels : aiVariationLabels;
  const aiCandidateKeys = useMemo(
    () => new Set(aiCandidates.map((candidate) => pointId(candidate.point))),
    [aiCandidates],
  );

  useEffect(() => {
    void store.refreshAiStatus();
    return () => store.cancelAi();
  }, [store]);

  useEffect(() => {
    if (!aiEnabled || selectedId === null || state.record === null || state.aiAvailable !== true) {
      return;
    }
    const baseMoveNumber = researchBase ?? moveNumber;
    const moves = researchBase === null ? [] : activeVariationMoves;
    const timer = window.setTimeout(() => {
      void store.analyze(selectedId, baseMoveNumber, moves).catch(() => undefined);
    }, 40);
    return () => window.clearTimeout(timer);
  }, [
    aiEnabled,
    activeVariationMoves,
    moveNumber,
    researchBase,
    selectedId,
    state.aiAvailable,
    state.aiSelectedConfigurationId,
    state.record,
    store,
  ]);

  useLayoutEffect(() => {
    const stage = boardStageRef.current;
    if (stage === null) return;
    const fitBoard = () => {
      const bounds = stage.getBoundingClientRect();
      const size = Math.floor(Math.min(bounds.width, bounds.height));
      stage.style.setProperty('--fox-board-size', `${Math.max(0, size)}px`);
    };
    fitBoard();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', fitBoard);
      return () => window.removeEventListener('resize', fitBoard);
    }
    const observer = new ResizeObserver(fitBoard);
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const synchronize = () => {
      if (document.fullscreenElement === null) setImmersive(false);
      else setImmersive(document.fullscreenElement === pageRef.current);
    };
    document.addEventListener('fullscreenchange', synchronize);
    return () => document.removeEventListener('fullscreenchange', synchronize);
  }, []);

  useEffect(() => {
    if (!immersive) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && document.fullscreenElement === null) setImmersive(false);
    };
    window.addEventListener('keydown', onEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onEscape);
    };
  }, [immersive]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        event.isComposing ||
        (target instanceof HTMLElement &&
          (target.isContentEditable || target.closest('input, textarea, select')))
      )
        return;
      if (frames.length === 0 || researching || detailsOpen || foxSearchOpen || annotationOpen) {
        return;
      }
      if (event.key === 'ArrowLeft') setMoveNumber((current) => Math.max(0, current - 1));
      if (event.key === 'ArrowRight') {
        setMoveNumber((current) => Math.min(frames.length - 1, current + 1));
      }
      if (event.key === 'Home') setMoveNumber(0);
      if (event.key === 'End') setMoveNumber(frames.length - 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [annotationOpen, detailsOpen, foxSearchOpen, frames.length, researching]);

  useEffect(() => {
    if (captureMessage === null) return;
    const timer = window.setTimeout(() => setCaptureMessage(null), 1_800);
    return () => window.clearTimeout(timer);
  }, [captureMessage]);

  const endResearch = () => {
    store.cancelAi();
    setResearchBase(null);
    setVariationMoves([]);
    setResearchCursor(0);
    store.clearResearch();
  };

  const search = (nextPage = 1, newQuery = true) => {
    const input = newQuery ? searchInput : activeSearch;
    if (input === null || input.query.length < 2) return;
    dispatchReflection({ type: 'close' });
    if (newQuery || nextPage === 1) {
      setActiveSearch(input);
      setSelectedId(null);
      setMoveNumber(0);
      setDetailsOpen(false);
    }
    endResearch();
    void store
      .search({ ...input, page: nextPage, pageSize: 2 })
      .then(() => {
        foxSearchPanel.resetPosition();
        setFoxSearchOpen(false);
      })
      .catch(() => undefined);
  };

  const load = (record: FoxKifuRecordSummary) => {
    dispatchReflection({ type: 'close' });
    endResearch();
    setAnnotationOpen(false);
    setAnnotations([]);
    setSelectedId(record.recordId);
    setMoveNumber(0);
    setDetailsOpen(false);
    void store
      .loadRecord(record.recordId)
      .then(() => {
        setMoveNumber(0);
        if (store.snapshot().record?.record.recordId === record.recordId) {
          dispatchReflection({ type: 'open', recordId: record.recordId });
        }
      })
      .catch(() => undefined);
  };

  const openDetails = () => {
    if (activeSearch === null) return;
    setDetailsOpen(true);
    void store.searchDetails({ ...activeSearch, page: 1, pageSize: 10 }).catch(() => undefined);
  };

  const loadDetailPage = (nextPage: number) => {
    if (activeSearch === null) return;
    void store
      .searchDetails({ ...activeSearch, page: nextPage, pageSize: 10 })
      .catch(() => undefined);
  };

  const placeResearch = (point: BoardPoint) => {
    if (selectedId === null || researchBase === null || state.pending !== null) return;
    const next = branchResearchVariation(variationMoves, researchCursor, point);
    store.cancelAi();
    void store
      .research(selectedId, researchBase, next)
      .then(() => {
        setVariationMoves(next);
        setResearchCursor(next.length);
      })
      .catch(() => undefined);
  };

  const navigateResearch = (cursor: number) => {
    if (selectedId === null || researchBase === null || state.pending !== null) return;
    const target = Math.max(0, Math.min(variationMoves.length, Math.trunc(cursor)));
    if (target === researchCursor) return;
    store.cancelAi();
    if (target === 0) {
      store.clearResearch();
      setResearchCursor(0);
      return;
    }
    const moves = researchVariationPrefix(variationMoves, target);
    void store
      .research(selectedId, researchBase, moves)
      .then(() => setResearchCursor(target))
      .catch(() => undefined);
  };

  const selectAiCandidate = (point: BoardPoint) => {
    if (selectedId === null || state.pending !== null || state.aiPending) return;
    if (researchBase !== null) {
      placeResearch(point);
      return;
    }
    const next = [point];
    store.cancelAi();
    setResearchBase(moveNumber);
    setVariationMoves([]);
    setResearchCursor(0);
    store.clearResearch();
    void store
      .research(selectedId, moveNumber, next)
      .then(() => {
        setVariationMoves(next);
        setResearchCursor(next.length);
      })
      .catch(() => {
        setResearchBase(null);
        setVariationMoves([]);
        setResearchCursor(0);
      });
  };

  const toggleAi = async () => {
    if (aiEnabled) {
      setAiEnabled(false);
      store.cancelAi();
      return;
    }
    if (state.aiAvailable === true) {
      if (state.record === null) {
        setAiConnectionOpen(true);
        return;
      }
      setAiEnabled(true);
      return;
    }
    setAiConnectionOpen(true);
    await checkAiConnection();
  };

  const checkAiConnection = async () => {
    setAiChecking(true);
    await store.refreshAiStatus();
    setAiChecking(false);
    if (store.snapshot().aiAvailable === true) {
      setAiConnectionOpen(false);
      setAiEnabled(true);
    }
  };

  const toggleResearch = () => {
    if (researching) {
      endResearch();
      return;
    }
    if (state.record === null) return;
    store.clearAiVariation();
    setResearchBase(moveNumber);
    setVariationMoves([]);
    setResearchCursor(0);
    store.clearResearch();
  };

  const cycleMarkerMode = () => {
    const current = MARKER_MODES.indexOf(markerMode);
    setMarkerMode(MARKER_MODES[(current + 1) % MARKER_MODES.length] ?? 'LAST_ONE');
  };

  const addStroke = (stroke: ReviewBrushStroke) => {
    setAnnotations((current) => [...current, stroke]);
  };

  const addPointAnnotation = (point: BoardPoint) => {
    if (annotationTool === 'BRUSH') return;
    setAnnotations((current) =>
      toggleReviewPointAnnotation(current, point, annotationTool, annotationColor),
    );
  };

  const toggleAnnotationPanel = () => {
    setAnnotationOpen((open) => !open);
  };

  const captureBoard = () => {
    const svg = boardStageRef.current?.querySelector<SVGSVGElement>('svg.go-board');
    if (svg === undefined || svg === null) return;
    void downloadBoardJpeg(svg, foxBoardCaptureFilename(selectedId ?? 'fox-kifu', moveNumber))
      .then(() => setCaptureMessage('当前棋盘已保存为 JPG 图片'))
      .catch((error: unknown) => setCaptureMessage(userErrorMessage(error, '棋盘截屏失败。')));
  };

  const toggleFullscreen = async () => {
    const pageElement = pageRef.current;
    if (pageElement === null) return;
    if (document.fullscreenElement === pageElement) {
      await document.exitFullscreen();
      return;
    }
    if (immersive) {
      setImmersive(false);
      return;
    }
    if (document.fullscreenElement === null && pageElement.requestFullscreen !== undefined) {
      await pageElement.requestFullscreen().catch(() => setImmersive(true));
      return;
    }
    setImmersive(true);
  };

  const foxToolbar = (
    <div className="student-review-controls fox-kifu-studio-controls">
      <div className="fox-kifu-tool-buttons" role="group" aria-label="棋谱分析工具">
        <button
          data-tool={FOX_KIFU_STUDENT_TOOL_ORDER[0]}
          type="button"
          disabled={state.record === null || researching}
          onClick={cycleMarkerMode}
        >
          <TargetIcon aria-hidden="true" />
          <span>标记：{MARKER_LABELS[markerMode]}</span>
        </button>
        <button
          data-tool={FOX_KIFU_STUDENT_TOOL_ORDER[1]}
          type="button"
          className={researching ? 'is-active' : ''}
          disabled={state.record === null || state.pending === 'RESEARCH' || annotationOpen}
          onClick={toggleResearch}
        >
          <ResetIcon aria-hidden="true" />
          <span>{researching ? '结束试下' : '对局研究'}</span>
        </button>
        <button
          data-tool={FOX_KIFU_STUDENT_TOOL_ORDER[2]}
          type="button"
          className={annotationOpen ? 'is-active' : ''}
          disabled={state.record === null}
          onClick={toggleAnnotationPanel}
        >
          <Pencil2Icon aria-hidden="true" />
          <span>添加标记</span>
        </button>
        <button
          data-tool={FOX_KIFU_STUDENT_TOOL_ORDER[3]}
          type="button"
          className={`${aiEnabled ? 'is-active' : ''}${state.aiPending ? ' is-loading' : ''}`}
          title={
            state.aiAvailable === false
              ? state.aiAuthorized === false
                ? '当前账号未获得 AI 算力权限'
                : '智子云算力尚未配置或工作进程未连接'
              : state.record === null
                ? '智子云已连接；选择棋谱后可显示推荐点'
                : aiEnabled
                  ? '持续分析已开启；点击暂停会中断当前任务'
                  : '显示当前局面的 AI 推荐点'
          }
          onClick={() => void toggleAi()}
        >
          <LightningBoltIcon aria-hidden="true" />
          <span>
            {state.aiPending
              ? state.aiAnalysis?.status === 'QUEUED'
                ? '算力排队中…'
                : '连接 / 分析中…'
              : aiEnabled
                ? '暂停AI'
                : 'AI算力'}
          </span>
        </button>
        <button
          data-tool={FOX_KIFU_STUDENT_TOOL_ORDER[4]}
          type="button"
          className={foxSearchOpen ? 'is-active' : ''}
          aria-expanded={foxSearchOpen}
          onClick={() => {
            if (foxSearchOpen) foxSearchPanel.resetPosition();
            setFoxSearchOpen((open) => !open);
          }}
        >
          <ListBulletIcon aria-hidden="true" />
          <span>野狐棋谱</span>
        </button>
        <button
          data-tool={FOX_KIFU_STUDENT_TOOL_ORDER[5]}
          type="button"
          disabled={state.record === null}
          onClick={captureBoard}
        >
          <CameraIcon aria-hidden="true" />
          <span>截图</span>
        </button>
      </div>
      <div className="student-review-navigation" role="group" aria-label="棋谱前进与后退">
        <button
          type="button"
          disabled={
            researching ? researchCursor === 0 || state.pending === 'RESEARCH' : moveNumber === 0
          }
          onClick={() => (researching ? navigateResearch(0) : setMoveNumber(0))}
        >
          <DoubleArrowLeftIcon aria-hidden="true" />
          {researching ? '变化起点' : '首手'}
        </button>
        <button
          type="button"
          disabled={
            researching ? researchCursor === 0 || state.pending === 'RESEARCH' : moveNumber === 0
          }
          onClick={() =>
            researching
              ? navigateResearch(researchCursor - 1)
              : setMoveNumber((current) => Math.max(0, current - 1))
          }
        >
          <ArrowLeftIcon aria-hidden="true" />
          {researching ? '后退' : '上一手'}
        </button>
        <span className="student-review-move-status" aria-live="polite">
          <strong>
            {researching
              ? `变化 ${researchCursor} / ${variationMoves.length} 手`
              : `第 ${moveNumber} / ${totalMoves} 手`}
          </strong>
          <small>{state.record?.record.resultText ?? '等待载入'}</small>
        </span>
        <button
          type="button"
          disabled={
            researching
              ? researchCursor >= variationMoves.length || state.pending === 'RESEARCH'
              : frames.length === 0 || moveNumber >= totalMoves
          }
          onClick={() =>
            researching
              ? navigateResearch(researchCursor + 1)
              : setMoveNumber((current) => Math.min(totalMoves, current + 1))
          }
        >
          {researching ? '前进' : '下一手'}
          <ArrowRightIcon aria-hidden="true" />
        </button>
        <button
          type="button"
          disabled={
            researching
              ? researchCursor >= variationMoves.length || state.pending === 'RESEARCH'
              : frames.length === 0 || moveNumber >= totalMoves
          }
          onClick={() =>
            researching ? navigateResearch(variationMoves.length) : setMoveNumber(totalMoves)
          }
        >
          {researching ? '变化末手' : '末手'}
          <DoubleArrowRightIcon aria-hidden="true" />
        </button>
      </div>
      {aiEnabled ? <span className="fox-kifu-ai-active-state">持续分析中</span> : null}
      {aiEnabled && state.aiError !== null ? (
        <button className="fox-kifu-ai-error" type="button" onClick={() => store.clearAiError()}>
          {state.aiError}
        </button>
      ) : null}
    </div>
  );

  return (
    <main
      ref={pageRef}
      className={`board-workspace board-workspace--student fox-kifu-page fox-kifu-page--sidebar-tools game-review-studio student-review-studio fox-kifu-studio${immersive ? ' fox-kifu-page--immersive' : ''}`}
    >
      <div className="fox-kifu-workspace">
        <aside className="fox-kifu-move-rail" aria-label="棋谱手数导航">
          <button
            className="back-button fox-kifu-back return-button"
            type="button"
            onClick={onBack}
          >
            ← 返回大厅
          </button>
          {(['BLACK', 'WHITE'] as const).map((color) => {
            const player = playerForColor(selectedSummary, color);
            const fallbackName =
              color === 'BLACK' ? state.record?.record.blackName : state.record?.record.whiteName;
            return (
              <div className="fox-kifu-player-badge" key={color}>
                <span className={`stone-preview stone-preview--${color.toLowerCase()}`} />
                <strong>
                  {player?.nickname ?? fallbackName ?? (color === 'BLACK' ? '黑方' : '白方')}
                </strong>
                <small>{player?.rank ?? ''}</small>
              </div>
            );
          })}
          <div className="fox-kifu-vertical-progress">
            <strong>{researching ? researchCursor : moveNumber}</strong>
            <VerticalMoveSlider
              max={researching ? variationMoves.length : totalMoves}
              value={researching ? researchCursor : moveNumber}
              disabled={
                frames.length === 0 ||
                (researching && (variationMoves.length === 0 || state.pending === 'RESEARCH'))
              }
              onChange={researching ? navigateResearch : setMoveNumber}
            />
            <small>/ {researching ? variationMoves.length : totalMoves}</small>
          </div>
          <BoardMoveProgress
            value={researching ? researchCursor : moveNumber}
            max={researching ? variationMoves.length : totalMoves}
            disabled={
              frames.length === 0 ||
              (researching && (variationMoves.length === 0 || state.pending === 'RESEARCH'))
            }
            onChange={researching ? navigateResearch : setMoveNumber}
          />
        </aside>

        <section className="fox-kifu-board-panel">
          <div ref={boardStageRef} className="fox-kifu-board-stage">
            <div className="teacher-board-frame">
              <BoardSurface
                stoneAppearance="shell"
                coordinateEdges={{ top: true, left: true }}
                size={boardSize}
                stones={displayedStones}
                lastMove={
                  state.aiVariation === null &&
                  stoneLabels.length === 0 &&
                  originalFrame?.kind === 'PLAY'
                    ? originalFrame.point
                    : null
                }
                stoneLabels={displayedLabels}
                aiCandidates={state.aiVariation === null ? aiCandidates : []}
                annotationLayer={
                  <ReviewAnnotationLayer
                    size={boardSize}
                    open={annotationOpen}
                    tool={annotationTool}
                    color={annotationColor}
                    width={annotationWidth}
                    annotations={annotations}
                    onStroke={addStroke}
                    onPoint={addPointAnnotation}
                  />
                }
                {...(state.aiVariation !== null
                  ? {}
                  : annotationOpen
                    ? {}
                    : researching
                      ? {
                          isActionable: (_point: BoardPoint, stone: StoneColor | null) =>
                            stone === null && state.pending !== 'RESEARCH',
                          onPoint: placeResearch,
                        }
                      : aiEnabled && aiCandidates.length > 0
                        ? {
                            isActionable: (point: BoardPoint, stone: StoneColor | null) =>
                              stone === null && aiCandidateKeys.has(pointId(point)),
                            onPoint: selectAiCandidate,
                          }
                        : {})}
              />
            </div>
            {state.record === null ? (
              <div className="fox-kifu-board-empty">
                <strong>搜索并选择一盘棋谱</strong>
                <span>棋谱将在这里按服务端复演结果呈现</span>
              </div>
            ) : null}
            {captureMessage !== null ? (
              <div className="fox-kifu-capture-toast">{captureMessage}</div>
            ) : null}
            {detailsOpen ? (
              <FoxKifuRecordDetailsDialog
                state={state.detailSearch}
                pending={state.pending}
                page={detailPage}
                totalPages={detailPages}
                selectedId={selectedId}
                onClose={() => setDetailsOpen(false)}
                onPage={loadDetailPage}
                onSelect={load}
              />
            ) : null}
            {aiConnectionOpen ? (
              <section
                className="fox-kifu-details-backdrop"
                role="presentation"
                onMouseDown={(event) => {
                  if (event.currentTarget === event.target) setAiConnectionOpen(false);
                }}
              >
                <div
                  className="fox-kifu-ai-dialog"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="fox-kifu-ai-dialog-title"
                >
                  <header>
                    <span>
                      <small>ZHIZI CLOUD COMPUTE</small>
                      <h2 id="fox-kifu-ai-dialog-title">智子云 AI 算力</h2>
                    </span>
                    <button type="button" onClick={() => setAiConnectionOpen(false)}>
                      关闭
                    </button>
                  </header>
                  <div className="fox-kifu-ai-connection-status" aria-live="polite">
                    <i className={state.aiAvailable === true ? 'is-ready' : ''} />
                    <span>
                      <strong>
                        {aiChecking
                          ? '正在检测连接…'
                          : state.aiAvailable === true
                            ? '算力任务服务可用'
                            : state.aiServiceEnabled === false
                              ? '平台 AI 算力总开关未启用'
                              : state.aiAuthorized === false
                                ? '当前账号未开通 AI 算力'
                                : state.aiConfigured === false
                                  ? '教师尚未配置智子云算力'
                                  : '智子云算力暂时离线'}
                      </strong>
                      <small>
                        {state.aiAvailable === true
                          ? '可提交分析；首次使用时远程引擎会按当前档位启动。'
                          : state.aiServiceEnabled === false
                            ? '请联系平台管理员启用部署环境中的 AI 算力服务。'
                            : state.aiAuthorized === false
                              ? '请联系平台管理员为当前学生账号授权。'
                              : '连接信息由平台管理员统一配置和维护。'}
                      </small>
                    </span>
                  </div>
                  <AiAccountSelector
                    state={state}
                    onSelect={(id) => store.selectAiAccount(id)}
                    onSaveDefault={() => store.saveAiDefault()}
                  />
                  {state.aiProfile !== null ? (
                    <dl className="fox-kifu-ai-profile">
                      <div>
                        <dt>算力模式</dt>
                        <dd>{ZHIZI_COMPUTE_MODE_LABELS[state.aiProfile.computeMode]}</dd>
                      </div>
                      <div>
                        <dt>档位 / 权重</dt>
                        <dd>
                          {state.aiProfile.exclusiveTier === null
                            ? '共享档'
                            : ZHIZI_EXCLUSIVE_TIER_LABELS[state.aiProfile.exclusiveTier]}{' '}
                          · {zhiziWeightLabel(state.aiProfile.engineWeight)}
                        </dd>
                      </div>
                      <div>
                        <dt>分析参数</dt>
                        <dd>
                          {state.aiProfile.maxVisits === 0
                            ? '不封顶'
                            : `${state.aiProfile.maxVisits} visits`}{' '}
                          · {state.aiProfile.recommendationCount} 个推荐点
                        </dd>
                      </div>
                    </dl>
                  ) : null}
                  <div className="fox-kifu-ai-security-note">
                    <strong>平台统一管理</strong>
                    <p>
                      智子云账号和密码只由教师在教师端配置。学生端只能检测和使用已授权的 AI
                      算力，不会看到、填写或保存任何智子云登录信息。
                    </p>
                  </div>
                  <footer>
                    <button type="button" onClick={() => setAiConnectionOpen(false)}>
                      关闭
                    </button>
                    <button
                      className="primary-button"
                      type="button"
                      disabled={aiChecking}
                      onClick={() => void checkAiConnection()}
                    >
                      {aiChecking ? '正在检测…' : '重新检测并连接'}
                    </button>
                  </footer>
                </div>
              </section>
            ) : null}
          </div>
        </section>

        <aside className="fox-kifu-data-panel">
          <header className="fox-kifu-data-title">
            <h1>野狐棋谱</h1>
            {state.record !== null && reflectionRecord === null ? (
              <button
                type="button"
                className="fox-reflection-reopen"
                disabled={state.pending !== null}
                onClick={() => {
                  if (state.record)
                    dispatchReflection({ type: 'open', recordId: state.record.record.recordId });
                }}
              >
                <Pencil2Icon aria-hidden="true" />
                心得体会
              </button>
            ) : null}
            <button
              className="secondary-button fullscreen-action"
              type="button"
              onClick={() => void toggleFullscreen()}
            >
              {immersive ? (
                <ExitFullScreenIcon aria-hidden="true" />
              ) : (
                <EnterFullScreenIcon aria-hidden="true" />
              )}
              {immersive ? '退出全屏' : '棋盘全屏'}
            </button>
          </header>
          <div className="workspace-tool-dock" aria-label="棋盘工具区">
            {aiEnabled ? (
              <AiCandidatePanel
                account={state.aiAnalysis?.account ?? selectedAccount(state)}
                onChangeAccount={() => setAiConnectionOpen(true)}
                candidates={aiCandidates}
                selectedRank={state.aiVariation?.candidateRank ?? null}
                variationPending={state.aiVariationPending}
                sharingStage={annotationOpen}
                onSelect={(rank) => {
                  if (state.aiVariation?.candidateRank === rank) {
                    store.clearAiVariation();
                    return;
                  }
                  const analysisId = state.aiAnalysis?.analysisId;
                  if (analysisId === undefined) return;
                  void store.loadAiVariation(analysisId, rank).catch(() => undefined);
                }}
              />
            ) : null}
            {annotationOpen ? (
              <ReviewAnnotationPanel
                tool={annotationTool}
                color={annotationColor}
                width={annotationWidth}
                annotationCount={annotations.length}
                sharingStage={aiEnabled}
                onTool={setAnnotationTool}
                onColor={setAnnotationColor}
                onWidth={setAnnotationWidth}
                onUndo={() => setAnnotations((current) => current.slice(0, -1))}
                onClear={() => setAnnotations([])}
                onClose={() => setAnnotationOpen(false)}
              />
            ) : null}
            {foxSearchOpen ? (
              <aside
                ref={foxSearchPanel.panelRef}
                className="fox-kifu-search-panel"
                style={foxSearchPanel.style}
                aria-label="野狐棋谱查询"
              >
                <header {...foxSearchPanel.dragHandlers}>
                  <strong>野狐棋谱</strong>
                  <span>搜索并选择公开棋谱</span>
                  {foxSearchPanel.floating ? (
                    <button type="button" onClick={foxSearchPanel.resetPosition}>
                      归位
                    </button>
                  ) : null}
                  <button
                    type="button"
                    aria-label="关闭野狐棋谱"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() => {
                      foxSearchPanel.resetPosition();
                      setFoxSearchOpen(false);
                    }}
                  >
                    关闭
                  </button>
                </header>
                <form
                  onSubmit={(event: FormEvent) => {
                    event.preventDefault();
                    search(1);
                  }}
                >
                  <label>
                    野狐昵称或 UID
                    <input
                      value={query}
                      maxLength={64}
                      placeholder="例如：柯洁 或 6757425"
                      onChange={(event) => setQuery(event.currentTarget.value)}
                    />
                  </label>
                  <div className="fox-kifu-date-row">
                    <label>
                      开始日期
                      <input
                        type="date"
                        value={from}
                        onChange={(event) => setFrom(event.currentTarget.value)}
                      />
                    </label>
                    <label>
                      结束日期
                      <input
                        type="date"
                        value={to}
                        onChange={(event) => setTo(event.currentTarget.value)}
                      />
                    </label>
                  </div>
                  <button
                    className="primary-button"
                    type="submit"
                    disabled={state.pending !== null || query.trim().length < 2}
                  >
                    {state.pending === 'SEARCH' ? '正在检索…' : '查询野狐棋谱'}
                  </button>
                </form>
                {state.error !== null ? (
                  <p className="message message--error" role="alert">
                    {state.error}
                  </p>
                ) : null}
              </aside>
            ) : null}
          </div>
          {reflectionRecord !== null ? (
            <FoxKifuReflectionCard
              key={reflectionRecord.recordId}
              initialHtml={reflection.drafts.get(reflectionRecord.recordId) ?? ''}
              recordLabel={`${reflectionRecord.blackName}（黑） · ${reflectionRecord.whiteName}（白）`}
              onChange={(html) =>
                dispatchReflection({ type: 'edit', recordId: reflectionRecord.recordId, html })
              }
              onClose={() => dispatchReflection({ type: 'close' })}
            />
          ) : null}
          <div
            className="fox-kifu-insights"
            aria-label="野狐棋谱数据与列表"
            hidden={reflectionRecord !== null}
          >
            <StatisticsPanel state={state.search} />
            <section className="fox-kifu-search-results" aria-label="棋谱列表">
              <div className="fox-kifu-section-heading fox-kifu-list-heading">
                <span>
                  <strong>
                    棋谱列表 <em>{state.search === null ? '0盘' : `${state.search.total}盘`}</em>
                  </strong>
                </span>
                <button type="button" disabled={state.search === null} onClick={openDetails}>
                  查看详细
                </button>
              </div>
              <div className="fox-kifu-list-head" aria-hidden="true">
                <span>日期</span>
                <span>黑方</span>
                <span>白方</span>
                <span>结果</span>
              </div>
              <div className="fox-kifu-record-list">
                {state.search?.records.map((record) => (
                  <button
                    key={record.recordId}
                    type="button"
                    className={selectedId === record.recordId ? 'is-selected' : ''}
                    aria-current={selectedId === record.recordId ? 'true' : undefined}
                    disabled={state.pending !== null && state.pending !== record.recordId}
                    onClick={() => load(record)}
                  >
                    <span>{formatDate(record.playedAt)}</span>
                    <span>{record.black.nickname}</span>
                    <span>{record.white.nickname}</span>
                    <span>{state.pending === record.recordId ? '载入中…' : record.resultText}</span>
                  </button>
                ))}
                {state.search !== null && state.search.records.length === 0 ? (
                  <p className="history-empty">该时间范围内没有公开棋谱。</p>
                ) : null}
                {state.search === null ? (
                  <p className="history-empty">输入野狐昵称或 UID 后开始查询。</p>
                ) : null}
              </div>
              {state.search !== null ? (
                <div className="fox-kifu-pagination">
                  <button
                    type="button"
                    disabled={page <= 1 || state.pending !== null}
                    onClick={() => search(page - 1, false)}
                  >
                    上一页
                  </button>
                  <span>
                    第 {page} / {totalPages} 页
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages || state.pending !== null}
                    onClick={() => search(page + 1, false)}
                  >
                    下一页
                  </button>
                </div>
              ) : null}
            </section>
            {!foxSearchOpen && state.error !== null ? (
              <p className="message message--error" role="alert">
                {state.error}
              </p>
            ) : null}
          </div>
          {reflectionRecord !== null && !foxSearchOpen && state.error !== null ? (
            <p className="message message--error" role="alert">
              {state.error}
            </p>
          ) : null}
          {foxToolbar}
        </aside>
      </div>
    </main>
  );
}

/** Shared detailed-record picker used by both student review and teacher classroom presentation. */
export function FoxKifuRecordDetailsDialog({
  state,
  pending,
  page,
  totalPages,
  selectedId,
  onClose,
  onPage,
  onSelect,
}: {
  readonly state: ReturnType<FoxKifuStore['snapshot']>['detailSearch'];
  readonly pending: ReturnType<FoxKifuStore['snapshot']>['pending'];
  readonly page: number;
  readonly totalPages: number;
  readonly selectedId: string | null;
  readonly onClose: () => void;
  readonly onPage: (page: number) => void;
  readonly onSelect: (record: FoxKifuRecordSummary) => void;
}) {
  return (
    <div className="fox-kifu-details-backdrop">
      <section
        className="fox-kifu-details-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="详细棋谱列表"
      >
        <header>
          <div>
            <small>每页 10 盘</small>
            <h2>详细棋谱</h2>
          </div>
          <button type="button" onClick={onClose}>
            关闭
          </button>
        </header>
        <div className="fox-kifu-detail-table">
          <div className="fox-kifu-detail-head" aria-hidden="true">
            <span>日期</span>
            <span>开始时间</span>
            <span>总用时</span>
            <span>棋局类型</span>
            <span>黑方</span>
            <span>白方</span>
            <span>结果</span>
          </div>
          <div className="fox-kifu-detail-list">
            {state?.records.map((record) => (
              <button
                type="button"
                key={record.recordId}
                className={[
                  selectedId === record.recordId ? 'is-selected' : '',
                  isFlyingKnifeRecord(record) ? 'is-flying-knife' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                disabled={pending !== null && pending !== record.recordId}
                onClick={() => onSelect(record)}
              >
                <span data-label="日期">{formatDate(record.playedAt)}</span>
                <span data-label="开始时间">{formatStartTime(record.playedAt)}</span>
                <span data-label="总用时">{formatGameDuration(record.durationSeconds)}</span>
                <span data-label="棋局类型">{record.gameTypeLabel}</span>
                <span data-label="黑方">{record.black.nickname}</span>
                <span data-label="白方">{record.white.nickname}</span>
                <span data-label="结果">
                  {pending === record.recordId ? '载入中…' : record.resultText}
                </span>
              </button>
            ))}
            {pending === 'DETAILS' && state === null ? <p>正在载入详细棋谱…</p> : null}
            {state !== null && state.records.length === 0 ? <p>该时间范围内没有棋谱。</p> : null}
          </div>
        </div>
        <footer>
          <span>共 {state?.total ?? 0} 盘</span>
          <div>
            <button
              type="button"
              disabled={page <= 1 || pending !== null}
              onClick={() => onPage(page - 1)}
            >
              上一页
            </button>
            <strong>
              {page} / {totalPages}
            </strong>
            <button
              type="button"
              disabled={page >= totalPages || pending !== null}
              onClick={() => onPage(page + 1)}
            >
              下一页
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}

function StatisticsPanel({
  state,
}: {
  readonly state: ReturnType<FoxKifuStore['snapshot']>['search'];
}) {
  const statistics = state?.statistics;
  const total = statistics?.total ?? 0;
  const excludedFlyingKnife = Math.max(0, (state?.total ?? 0) - total);
  const percent = (value: number) => (total === 0 ? 0 : (value / total) * 100);
  const displayedPercent = (value: number) => foxKifuStatisticsPercent(value, total);
  const rangeLabel =
    state === null
      ? '查询后显示真实统计'
      : `${state.range.from ?? '最早记录'} 至 ${state.range.to ?? '最新记录'}`;
  return (
    <section className="fox-kifu-panel-card fox-kifu-statistics-card">
      <div className="fox-kifu-section-heading">
        <span>
          <small>PERIOD OVERVIEW</small>
          <strong>用时与胜率</strong>
        </span>
        {state === null ? (
          <em>等待查询</em>
        ) : !state.scanComplete ? (
          <em>部分数据</em>
        ) : (
          <em>{state.player.nickname}</em>
        )}
      </div>
      <p className="fox-kifu-stat-range">{rangeLabel}</p>
      <div className="fox-kifu-stat-grid">
        <span>
          <small>有效对局</small>
          <strong>{total}</strong>
        </span>
        <span>
          <small>总胜率</small>
          <strong>{statistics?.winRate ?? 0}%</strong>
        </span>
        <span>
          <small>执黑 / 执白</small>
          <strong>
            {statistics?.asBlack ?? 0} / {statistics?.asWhite ?? 0}
          </strong>
        </span>
      </div>
      <div className="fox-kifu-stat-section-heading">
        <strong>战绩分布</strong>
        <span>数量 · 占比</span>
      </div>
      {[
        ['胜局', statistics?.wins ?? 0, 'win'],
        ['负局', statistics?.losses ?? 0, 'loss'],
        ['其他', statistics?.others ?? 0, 'other'],
      ].map(([label, value, kind]) => (
        <div className="fox-kifu-result-row" key={label}>
          <span>{label}</span>
          <div>
            <i className={`is-${kind}`} style={{ width: `${percent(Number(value))}%` }} />
          </div>
          <strong>
            {value} · {displayedPercent(Number(value))}%
          </strong>
        </div>
      ))}
      <div className="fox-kifu-stat-section-heading">
        <strong>对局用时分布</strong>
        <span>公开记录</span>
      </div>
      <div className="fox-kifu-duration-grid">
        {[
          ['超过30分钟', statistics?.over30Minutes ?? 0],
          ['15至30分钟', statistics?.between15And30Minutes ?? 0],
          ['低于15分钟', statistics?.under15Minutes ?? 0],
        ].map(([label, value]) => (
          <span key={label}>
            <small>{label}</small>
            <strong>{value}</strong>
            <em>{displayedPercent(Number(value))}%</em>
          </span>
        ))}
      </div>
      <div className="fox-kifu-stat-section-heading">
        <strong>执子表现</strong>
        <span>对局数 · 胜率</span>
      </div>
      <div className="fox-kifu-color-rates">
        <span>
          <small>执黑胜率</small>
          <strong>{statistics?.blackWinRate ?? 0}%</strong>
          <em>
            {statistics?.asBlack ?? 0} 盘 · {statistics?.blackWins ?? 0} 胜
          </em>
          <i>
            <b style={{ width: `${statistics?.blackWinRate ?? 0}%` }} />
          </i>
        </span>
        <span>
          <small>执白胜率</small>
          <strong>{statistics?.whiteWinRate ?? 0}%</strong>
          <em>
            {statistics?.asWhite ?? 0} 盘 · {statistics?.whiteWins ?? 0} 胜
          </em>
          <i>
            <b style={{ width: `${statistics?.whiteWinRate ?? 0}%` }} />
          </i>
        </span>
      </div>
      {(statistics?.unknownDuration ?? 0) > 0 ? (
        <p className="fox-kifu-duration-note">
          另有 {statistics?.unknownDuration} 盘缺少公开用时。
        </p>
      ) : null}
      {excludedFlyingKnife > 0 ? (
        <p className="fox-kifu-duration-note">
          已排除 {excludedFlyingKnife} 盘飞刀棋谱，不纳入用时与胜率统计。
        </p>
      ) : null}
    </section>
  );
}
